import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, debounceTime, Observable, Subject, switchMap, tap, of, map, BehaviorSubject } from 'rxjs';
import { PricePolicySearchType } from 'src/app/shared/enums/price-policy-search-type.enum';
import { FilteringService } from 'src/app/shared/services/filtering.service';
import { PricePolicyV2Service } from 'src/app/shared/services/price-policy-v2.service';
import { PricePolicySearchEvent, PricePolicy } from '../interfaces/price-policy';
import { PagedResult, PagingOptions } from 'src/app/shared/models/paging';
import { Organization } from 'src/app/features/organization/organization-models';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { ExpandListSort } from 'ok-angular-shared/components';

interface FilterState {
  query: string | null;
  organizationId: number | null;
  policySearchType: PricePolicySearchType;
  returnGlobal: boolean;
  pricePolicyId: number | null;
  sort: Sort | null;
  page: PageEvent;
  type: string | null;
}

export enum PricePolicyQueryParams {
  query = 'query',
  organizationId = 'organizationId',
  policySearchType = 'policySearchType',
  returnGlobal = 'returnGlobal',
  pricePolicyId = 'pricePolicyId',
}
@Injectable()
export class PricePolicyListStateService extends PagerBase {
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  filteringService = inject(FilteringService);
  pricePolicyService = inject(PricePolicyV2Service);

  readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);
  public focusedPricePolicy: WritableSignal<PricePolicy | null> = signal(null);
  public loading: WritableSignal<boolean> = signal<boolean>(false);
  private refreshTrigger = signal(0);
  private pendingHighlightName = signal<string | null>(null);
  public refreshPricePolicySubject = new Subject<PricePolicy>();
  public pagingOptions: PagingOptions = {
    pageSize: this.defaultPageSize,
    pageSizeOptions: this.pageSizes,
  };
  public currentSort = signal<ExpandListSort<PricePolicy>>({ column: 'name', direction: 'asc' });
  public pricePoliciesState = signal<PagedResult<PricePolicy>>({
    items: [],
    totalCount: 0,
    pageIndex: 0,
    pageSize: this.pagingOptions.pageSize,
  });
  public filters: WritableSignal<FilterState> = signal<FilterState>({
    query: this.queryParamMap$()?.get(PricePolicyQueryParams.query)
      ? decodeURIComponent(this.queryParamMap$()!.get(PricePolicyQueryParams.query)!)
      : null,
    organizationId: this.queryParamMap$()?.get(PricePolicyQueryParams.organizationId)
      ? parseInt(this.queryParamMap$()!.get(PricePolicyQueryParams.organizationId)!)
      : null,
    policySearchType: PricePolicySearchType.All,
    returnGlobal: true,
    pricePolicyId: this.queryParamMap$()?.get(PricePolicyQueryParams.pricePolicyId)
      ? parseInt(this.queryParamMap$()!.get(PricePolicyQueryParams.pricePolicyId)!)
      : null,
    sort: null,
    page: {
      length: 0,
      pageIndex: 0,
      pageSize: this.defaultPageSize,
    },
    type: null,
  });
  public pageSizeOptions = signal<number[]>(this.pageSizes);
  public resetAllFiltersTrigger = signal<{ reset: boolean }>({ reset: false });
  public fetchedOrganizations = signal<Organization[] | null>(null);

  constructor() {
    super();
    effect(() => {
      const filters = this.filters();
      const queryParams: Record<PricePolicyQueryParams, string | null> = {
        [PricePolicyQueryParams.policySearchType]: filters.policySearchType
          ? this.filteringService.safeCastEnum(PricePolicySearchType, filters.policySearchType.toString())
          : null,
        [PricePolicyQueryParams.organizationId]: filters.organizationId ? filters.organizationId.toString() : null,
        [PricePolicyQueryParams.pricePolicyId]: filters.pricePolicyId ? filters.pricePolicyId.toString() : null,
        [PricePolicyQueryParams.returnGlobal]: filters.returnGlobal ? filters.returnGlobal.toString() : null,
        [PricePolicyQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
      };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      });
    });

    // Subscribe to the observable to populate the signal
    this.$pricePolicies.subscribe();

    // Single-item refresh pipeline for highlighting
    this.refreshPricePolicySubject
      .pipe(
        switchMap(pricePolicy =>
          this.pricePolicyService
            .getPricePolicies({
              pricePolicyId: pricePolicy.id,
              paging: {
                length: 0,
                pageIndex: 0,
                pageSize: 1,
              },
            } as PricePolicySearchEvent)
            .pipe(
              map(result => result.items[0]),
              switchMap(freshPricePolicy =>
                freshPricePolicy ? of(freshPricePolicy) : this.pricePolicyService.getPricePolicy(pricePolicy.id)
              ),
              tap(freshPricePolicy => {
                const currentResult = this.pricePoliciesState();
                const itemIndex = currentResult.items.findIndex(s => s.id === pricePolicy.id);
                if (itemIndex === -1) return;

                const highlight = new BehaviorSubject<boolean>(true);
                const newItems = [...currentResult.items];
                newItems[itemIndex] = {
                  ...freshPricePolicy,
                  highlight: highlight,
                };

                this.pricePoliciesState.set({
                  ...currentResult,
                  items: newItems,
                });

                setTimeout(() => {
                  const currentResult = this.pricePoliciesState();
                  const currentIndex = currentResult.items.findIndex(s => s.id === pricePolicy.id);
                  if (currentIndex === -1) return;

                  const updatedItems = [...currentResult.items];
                  updatedItems[currentIndex] = {
                    ...updatedItems[currentIndex],
                    highlight: new BehaviorSubject<boolean>(false),
                  };

                  this.pricePoliciesState.set({
                    ...currentResult,
                    items: updatedItems,
                  });
                }, 3000);
              })
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  $pricePolicies: Observable<PagedResult<PricePolicy>> = combineLatest([
    toObservable(this.filters),
    toObservable(this.refreshTrigger),
  ]).pipe(
    debounceTime(300),
    tap(() => this.loading.set(true)),
    switchMap(([filters]) => {
      // When pricePolicyId is set, search only by pricePolicyId (ignoring other filters)
      if (filters.pricePolicyId) {
        const pricePolicySearchEvent = <PricePolicySearchEvent>{
          paging: { length: 0, pageIndex: 0, pageSize: 1 },
          pricePolicyId: filters.pricePolicyId,
          alwaysIncludeDynamic: false,
        };
        return this.pricePolicyService.getPricePolicies(pricePolicySearchEvent) as unknown as Observable<PagedResult<PricePolicy>>;
      }

      const pricePolicySearchEvent = <PricePolicySearchEvent>{
        paging: filters.page,
        sorting: filters.sort,
        query: filters.query ? filters.query : null,
        includeGlobal: filters.organizationId ? false : filters.returnGlobal,
        pricePolicyId: filters.pricePolicyId ? filters.pricePolicyId : null,
        organizationId: filters.organizationId ? filters.organizationId : null,
        onlyGlobal: filters.policySearchType === PricePolicySearchType.GlobalOnly,
        onlyPartnerPriceSet: filters.policySearchType === PricePolicySearchType.PartnerPriceOnly,
        type: filters.type,
        alwaysIncludeDynamic: false,
      };

      return this.pricePolicyService.getPricePolicies(pricePolicySearchEvent) as unknown as Observable<PagedResult<PricePolicy>>;
    }),
    tap(result => {
      if (result && result.items && result.items.length == 1 && this.filters().pricePolicyId) {
        this.focusedPricePolicy.set(result.items[0]);
      }

      // Preserve highlights from existing items when updating with fresh API data
      const currentItems = this.pricePoliciesState().items;
      const highlightName = this.pendingHighlightName();
      const itemsWithPreservedHighlights = result.items.map(freshItem => {
        // Highlight newly created item by name
        if (highlightName && freshItem.name === highlightName) {
          const highlight = new BehaviorSubject<boolean>(true);
          setTimeout(() => {
            const currentResult = this.pricePoliciesState();
            const currentIndex = currentResult.items.findIndex(s => s.id === freshItem.id);
            if (currentIndex === -1) return;

            const updatedItems = [...currentResult.items];
            updatedItems[currentIndex] = {
              ...updatedItems[currentIndex],
              highlight: new BehaviorSubject<boolean>(false),
            };

            this.pricePoliciesState.set({
              ...currentResult,
              items: updatedItems,
            });
          }, 3000);
          return { ...freshItem, highlight };
        }
        const existingItem = currentItems.find(item => item.id === freshItem.id);
        if (existingItem?.highlight) {
          return { ...freshItem, highlight: existingItem.highlight };
        }
        return freshItem;
      });
      this.pendingHighlightName.set(null);

      this.pricePoliciesState.set({
        ...result,
        items: itemsWithPreservedHighlights,
      });
      this.loading.set(false);
    })
  );

  public resetFilter() {
    this.filters.update(current => ({
      ...current,
      query: null,
      organizationId: null,
      pricePolicyId: null,
      returnGlobal: true,
      policySearchType: PricePolicySearchType.All,
      sort: null,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
      type: null,
    }));
    this.fetchedOrganizations.set(null);
    this.resetAllFiltersTrigger.set({ reset: true });
    this.focusedPricePolicy.set(null);
  }

  triggerUpdate() {
    this.refreshTrigger.update(n => n + 1);
  }

  triggerUpdateWithHighlight(name: string) {
    this.pendingHighlightName.set(name);
    this.filters.update(x => ({
      ...x,
      sort: { active: 'Created', direction: 'DESC' as Sort['direction'] },
      page: { ...x.page, pageIndex: 0 },
    }));
    this.refreshTrigger.update(n => n + 1);
  }

  public refreshPricePolicy(pricePolicy: PricePolicy) {
    this.refreshPricePolicySubject.next(pricePolicy);
  }

  onSortChange(sort: ExpandListSort<PricePolicy>) {
    this.loading.set(true);
    this.currentSort.set(sort);

    const matSort: Sort = {
      active: sort.column as string,
      direction: sort.direction.toUpperCase() as 'asc' | 'desc',
    };

    this.filters.update(x => ({
      ...x,
      sort: matSort,
      page: { ...x.page, pageIndex: 0 },
    }));
  }
}
